import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  ChatflowCsatConfigRequest,
  ChatflowCsatConfigResponse,
  ChatflowCsatMetricsResponse,
  ChatflowCsatResponseItem,
  ChatflowCsatSubmitRequest,
  ChatflowCsatSubmitResponse
} from '../models/chatflow-csat.model';
import { ChatflowRuntimeCsat } from '../models/chatflow-runtime.model';
import { finDelDiaIso, inicioDelDiaIso } from './chatflow-date-range.util';

@Injectable({ providedIn: 'root' })
export class ChatflowCsatApiService {
  private readonly base = `${environment.apiUrl}api/chatflows`;
  private readonly runtimeBase = `${environment.apiUrl}api/chatflows/runtime/conversations`;
  private readonly tokenBase = `${environment.apiUrl}api/chatflows/runtime/csat`;

  constructor(private http: HttpClient) {}

  // ── Config ─────────────────────────────────────────────────────────────────

  getConfig(chatflowId: number): Observable<ChatflowCsatConfigResponse> {
    return this.http.get<ChatflowCsatConfigResponse>(`${this.base}/${chatflowId}/csat-config`);
  }

  saveConfig(chatflowId: number, request: ChatflowCsatConfigRequest): Observable<ChatflowCsatConfigResponse> {
    return this.http.put<ChatflowCsatConfigResponse>(`${this.base}/${chatflowId}/csat-config`, request);
  }

  // ── Runtime (widget público) ────────────────────────────────────────────────

  submitCsat(conversationId: number, request: ChatflowCsatSubmitRequest): Observable<ChatflowCsatSubmitResponse> {
    return this.http.post<ChatflowCsatSubmitResponse>(`${this.runtimeBase}/${conversationId}/csat`, request);
  }

  // ── Encuesta del enlace enviado por correo (público, token de un solo uso) ──

  getCsatByToken(token: string): Observable<ChatflowRuntimeCsat> {
    return this.http.get<ChatflowRuntimeCsat>(`${this.tokenBase}/${token}`);
  }

  submitCsatByToken(token: string, respuesta: { rating: number; comment?: string }): Observable<ChatflowCsatSubmitResponse> {
    return this.http.post<ChatflowCsatSubmitResponse>(`${this.tokenBase}/${token}`, respuesta);
  }

  // ── Métricas ───────────────────────────────────────────────────────────────

  /**
   * El backend solo acepta `from` y `to` (ChatflowCsatController.getMetrics).
   * No existen filtros por equipo ni por agente: enviarlos sería mentirle al usuario.
   *
   * Las fechas del filtro son días (`yyyy-MM-dd`) y viajan como instantes que cubren el día
   * COMPLETO. Ver `chatflow-date-range.util`: enviarlas en crudo dejaba fuera las respuestas del
   * propio día seleccionado (inc51).
   */
  getMetrics(chatflowId: number, filters?: { from?: string; to?: string }): Observable<ChatflowCsatMetricsResponse> {
    let params = new HttpParams();
    if (filters?.from) params = params.set('from', inicioDelDiaIso(filters.from));
    if (filters?.to) params = params.set('to', finDelDiaIso(filters.to));
    return this.http.get<ChatflowCsatMetricsResponse>(`${this.base}/${chatflowId}/csat-metrics`, { params });
  }

  /**
   * Respuestas CSAT una a una, con su comentario (INC_CRM_019 (8) inc60).
   *
   * Las métricas agregadas no permiten llegar a la valoración concreta que disparó la alerta
   * al supervisor, que es justo a lo que apunta esa notificación.
   */
  getResponses(chatflowId: number, filters?: { from?: string; to?: string }): Observable<ChatflowCsatResponseItem[]> {
    let params = new HttpParams();
    if (filters?.from) params = params.set('from', inicioDelDiaIso(filters.from));
    if (filters?.to) params = params.set('to', finDelDiaIso(filters.to));
    return this.http.get<ChatflowCsatResponseItem[]>(`${this.base}/${chatflowId}/csat-responses`, { params });
  }
}
